import { Field, IconButton, Modal, Text, TextInput } from "@carbon6/c6-frontend-design";
import { useState, type ChangeEvent, type ReactNode, type SyntheticEvent } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import styles from "./DatasetMergeDialog.module.css";

// 통합 대상 표시에 필요한 값만 받는다(목록 행 전체를 전달하지 않는다).
export type DatasetMergeTarget = {
  uuid: string;
  displayName: string;
  totalEpisodes: number;
};

export type DatasetMergeDialogProps = {
  // 열 때 고정한 대상 스냅샷. 뒤에서 페이지를 넘겨도 통합 대상이 바뀌지 않는다.
  targets: readonly DatasetMergeTarget[];
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (displayName: string) => void;
};

// 데이터셋 통합 확인. 대상 목록을 그대로 보여 주고 결과 Dataset 의 표시명을 받는다.
// 서버가 부합 판정(채널 구조, embodiment)을 하므로 여기서 미리 막지 않고, 거절 사유를 errorMessage 로 표시한다.
export function DatasetMergeDialog({
  targets,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: DatasetMergeDialogProps): ReactNode {
  const [displayName, setDisplayName] = useState("");
  const trimmed = displayName.trim();
  const totalEpisodes = targets.reduce((sum, target) => sum + target.totalEpisodes, 0);

  function handleSubmit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault();
    if (submitting || trimmed === "") {
      return;
    }
    onSubmit(trimmed);
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      aria-labelledby="dataset-merge-title"
    >
      <HatModalHeader
        title="선택한 Dataset을 하나로 통합합니다."
        titleId="dataset-merge-title"
        close={{ disabled: submitting }}
      />
      <form className={styles.form} onSubmit={handleSubmit}>
        <Field label={`통합 대상 ${targets.length}개`} layout="stack">
          <ul className={styles.targets}>
            {targets.map((target) => (
              <li key={target.uuid} className={styles.target}>
                <Text variant={hatcheryTextModes["body-normal"]}>{target.displayName}</Text>
                <Text variant={hatcheryTextModes["caption-normal"]} className={styles.episodes}>
                  {`에피소드 ${target.totalEpisodes.toLocaleString()}개`}
                </Text>
              </li>
            ))}
          </ul>
        </Field>
        <Field label="새 Dataset 이름" layout="stack" disabled={submitting}>
          <TextInput
            value={displayName}
            maxLength={255}
            required
            placeholder="통합 결과 Dataset의 이름을 입력해 주세요."
            onChange={(event: ChangeEvent<HTMLInputElement>) => setDisplayName(event.target.value)}
          />
        </Field>
        <Text variant={hatcheryTextModes["caption-normal"]} className={styles.notice}>
          {`에피소드 ${totalEpisodes.toLocaleString()}개를 복사해 Dataset을 새로 만듭니다. 원본 Dataset은 그대로 남고, 통합본은 복사본이라 저장 용량은 그만큼 늘어납니다. 제외한 에피소드는 빠지고, Annotation 세트(Studio)와 random background 이미지는 함께 복사됩니다. 제외한 에피소드를 가리키는 Annotation 세트는 복사하지 않고, 렌더가 끝나지 않았거나 원본마다 해상도가 다른 카메라를 쓰는 세트는 draft로 복사됩니다. 그런 카메라의 random background 이미지도 복사하지 않습니다. 판정과 리뷰 기록(실패 표시·결함 태그·표기·결함 수용·코멘트·검토 완료)은 함께 옮깁니다. Parquet 진단은 통합본에서 다시 계산하고, 원본에서 최신인 영상 진단은 그대로 옮깁니다. 통합 내역은 새 Dataset 메모로 남습니다. Embodiment·Tip·라벨이 서로 다르면 그 값은 비운 채 리뷰 대기로 남습니다. 복사량에 따라 시간이 걸리며 백그라운드로 진행되고, 끝나면 알려드립니다.`}
        </Text>
        {errorMessage ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            {errorMessage}
          </Text>
        ) : null}
        <div className={styles.actions}>
          <IconButton
            text="취소"
            type="button"
            variant="surface"
            disabled={submitting}
            onClick={onClose}
          />
          <IconButton
            text={submitting ? "통합 요청 중" : "통합"}
            type="submit"
            variant="solid"
            disabled={submitting || trimmed === ""}
          />
        </div>
      </form>
    </Modal>
  );
}
